import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type ProjectSummary,
} from '../api/client';
import { isApiError } from '../api/errors';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { FormField } from '../components/FormField';
import { SelectField } from '../components/SelectField';
import { TextInput } from '../components/TextInput';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError, toFieldErrors } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import { StepEditor } from './StepEditor';
import {
  CRITICALITIES,
  KNOWN_JOURNEY_TYPES,
  newStepKey,
  toIngestInput,
  validateJourneyForm,
  type FieldErrors,
  type StepDraft,
} from './journeyAuthoring';
import styles from './JourneyForm.module.css';

type LoadState = 'loading' | 'error' | 'ready';

const TYPE_OPTIONS = [
  { value: '', label: 'Select a journey type…' },
  ...KNOWN_JOURNEY_TYPES.map((t) => ({ value: t, label: journeyTypeLabel(t) })),
];

const CRITICALITY_OPTIONS = CRITICALITIES.map((c) => ({
  value: c,
  label: c.charAt(0).toUpperCase() + c.slice(1),
}));

export interface JourneyFormProps {
  client?: ApiClient;
}

/**
 * Create or edit a customer-provided journey (#133). Loads the project (for the
 * verified host that targets are validated against) and, in edit mode, the journey
 * detail to prefill. Composes the header fields plus the keyboard-operable
 * {@link StepEditor}, validates client-side (mirroring the #21 caps), and POSTs the
 * ingest. Editing re-supplies a NEW version of the same journey type
 * (append-supersede, #21) — there is no in-place update or hard delete; the
 * journey type is therefore fixed while editing. A 409 surfaces a project-level
 * verification notice; entered data is preserved.
 */
export function JourneyForm({ client = defaultClient }: JourneyFormProps) {
  const { currentOrg } = useOrg();
  const { id, journeyId } = useParams<{ id: string; journeyId?: string }>();
  const navigate = useNavigate();
  useFocusMain();

  const mode: 'create' | 'edit' = journeyId ? 'edit' : 'create';
  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [project, setProject] = useState<ProjectSummary | null>(null);

  const [journeyType, setJourneyType] = useState('');
  const [title, setTitle] = useState('');
  const [criticality, setCriticality] = useState('medium');
  const [steps, setSteps] = useState<StepDraft[]>([]);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [summary, setSummary] = useState<{ message: string; requestId?: string } | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!orgId || !id) return;
      setPhase('loading');
      try {
        const nextProject = await client.getProject(orgId, id);
        if (!active) return;
        setProject(nextProject);
        setUnverified(nextProject.domainVerificationStatus !== 'verified');
        if (journeyId) {
          const detail = await client.getJourneyDetail(orgId, id, journeyId);
          if (!active) return;
          setJourneyType(detail.journeyType);
          setTitle(detail.title);
          setCriticality(detail.criticality);
          setSteps(
            detail.steps.map((step) => ({
              key: newStepKey(),
              actionKind: step.actionKind,
              description: step.description,
              targetPath: step.targetPath ?? '',
            })),
          );
        }
        setPhase('ready');
      } catch (err) {
        if (!active) return;
        setLoadMessage(toDisplayError(err).message);
        setPhase('error');
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [client, orgId, id, journeyId]);

  const announce = useCallback((message: string) => setStatusMessage(message), []);

  const submit = useCallback(async () => {
    if (!orgId || !id || !project || busy) return;
    const draft = { journeyType, title, criticality, steps };
    const errors = validateJourneyForm(draft, project.authorizedHost);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSummary({ message: 'Please fix the highlighted fields before saving.' });
      return;
    }

    setBusy(true);
    setSummary(null);
    try {
      await client.ingestJourneys(orgId, id, toIngestInput(draft));
      navigate(`/projects/${id}/journeys`);
    } catch (err) {
      if (isApiError(err) && err.code === 'conflict') {
        // Domain is not verified — a project-level gate, not a field error. Surface
        // it ONLY in the assertive, focused summary (not a second polite region).
        setSummary({
          message: 'Verify this domain before saving journeys.',
          ...(err.requestId ? { requestId: err.requestId } : {}),
        });
        return;
      }
      // Server 422s (targetPath/stepOrder) carry no step index, so surface them in
      // the summary rather than mis-attaching to a field. Client validation above
      // catches these before submit in the common case.
      const serverFields = toFieldErrors(err);
      const display = toDisplayError(err);
      const detail = Object.values(serverFields)[0];
      setSummary({
        message: detail ? `${display.message} ${detail}` : display.message,
        ...(display.requestId ? { requestId: display.requestId } : {}),
      });
    } finally {
      setBusy(false);
    }
  }, [orgId, id, project, busy, journeyType, title, criticality, steps, client, navigate]);

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 id="journey-form-heading" className={styles.heading}>
          {mode === 'edit' ? 'Edit journey' : 'New journey'}
        </h1>
        <p className={styles.subheading}>
          {mode === 'edit'
            ? 'Saving re-supplies a new version of this journey; the previous version is kept.'
            : 'Describe a customer journey your storefront supports, with its ordered steps.'}
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to={`/projects/${id ?? ''}/journeys`} className={styles.backLink}>
              Back to journeys
            </Link>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <form
            className={styles.form}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className={styles.summarySlot}>
              <InlineAlert
                ref={summaryRef}
                severity="error"
                {...(summary?.requestId ? { requestId: summary.requestId } : {})}
              >
                {summary?.message}
              </InlineAlert>
            </div>
            <InlineAlert id="journey-status" severity="success">
              {statusMessage}
            </InlineAlert>

            {unverified ? (
              <InlineAlert severity="info">
                This domain is not verified yet. Verify it before saving.{' '}
                <Link to={`/projects/${id ?? ''}/domain-verification`}>Verify domain</Link>
              </InlineAlert>
            ) : null}

            {mode === 'edit' ? (
              <div className={styles.readOnlyField}>
                <span className={styles.readOnlyLabel}>Journey type</span>
                <span className={styles.readOnlyValue}>{journeyTypeLabel(journeyType)}</span>
              </div>
            ) : (
              <SelectField
                label="Journey type"
                id="journey-type"
                value={journeyType}
                onChange={setJourneyType}
                options={TYPE_OPTIONS}
                {...(fieldErrors.journeyType ? { error: fieldErrors.journeyType } : {})}
              />
            )}

            <FormField
              label="Title"
              id="journey-title"
              {...(fieldErrors.title ? { error: fieldErrors.title } : {})}
            >
              {({ controlProps }) => (
                <TextInput
                  {...controlProps}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </FormField>

            <SelectField
              label="Criticality"
              id="journey-criticality"
              helpText="How business-critical this journey is. You attest this value."
              value={criticality}
              onChange={setCriticality}
              options={CRITICALITY_OPTIONS}
              {...(fieldErrors.criticality ? { error: fieldErrors.criticality } : {})}
            />

            {fieldErrors.steps ? (
              <InlineAlert severity="error">{fieldErrors.steps}</InlineAlert>
            ) : null}

            <StepEditor steps={steps} onChange={setSteps} errors={fieldErrors} announce={announce} />

            <div className={styles.actions}>
              <Button type="submit" busy={busy}>
                {busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Save journey'}
              </Button>
              <Link to={`/projects/${id ?? ''}/journeys`} className={styles.backLink}>
                Cancel
              </Link>
            </div>
          </form>
        ) : null}
      </Card>
    </AppShell>
  );
}
